<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useNotifications } from "@/composables/useNotifications";
import { ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types"
import { ChallengeMaterial } from "@/types/v2/task/challenge/material.types"
import { ChallengeDilution } from "@/types/v2/task/challenge/dilution.types"
import { getChallengeMaterialDisplayItems } from "@/utils/v2/tasks/display";

import { GroupTag, VaccineMethod } from "@/types/v2";
import { Tank, TankGroup } from "@/types/v2";


import JustificationModal from "@/components/v2/Modal/JustificationModal.vue";

interface Props {
    configurations: ChallengeConfiguration[],
    vaccineMethods: VaccineMethod[],
    challengeMaterials: ChallengeMaterial[],
    dilutions: ChallengeDilution[],
    tanks: Tank[]
}

// eslint-disable-next-line no-undef
const props = defineProps<Props>();

const { configurations: allConfigurations, updateConfiguration } = useChallengeConfigurations();
const { recordChange } = useTaskHistory();
const { availableTags } = useChallenge();
const { showSuccess, showError } = useNotifications();

// Local state
const tankGroups = ref<Record<string, TankGroup[]>>({});
const selectedTags = ref<Record<number, GroupTag | null>>({});
const isUpdating = ref<Record<number, boolean>>({});

// Helper to find the new tag (tag in destinationGroup but not in sourceGroup)
const findNewTag = (config: ChallengeConfiguration): GroupTag | null => {
    const sourceTags = config.sourceGroup?.tags || [];
    const destinationTags = config.destinationGroup?.tags || [];

    // Find tags in destination that are not in source (excluding Start Tag)
    const sourceTagTitles = new Set(
        sourceTags
            .filter(tag => tag.title !== 'Start Tag')
            .map(tag => tag.title)
    );

    const newTag = destinationTags.find(tag =>
        tag.title !== 'Start Tag' && !sourceTagTitles.has(tag.title)
    );

    return newTag || null;
};

// State for custom dropdown
const dropdownOpen = ref<Record<number, boolean>>({});

// Local state for instructions visibility
const showInstructions = ref<Record<number, boolean>>({});

// Initialize instructions as expanded by default
watch(() => props.configurations, (configs) => {
    if (configs) {
        configs.forEach((config, index) => {
            if (config.instructions && showInstructions.value[index] === undefined) {
                showInstructions.value[index] = true;
            }
        });
    }
}, { immediate: true });

// Toggle instructions visibility
const toggleInstructions = (configIndex: number) => {
    showInstructions.value[configIndex] = !showInstructions.value[configIndex];
};

const getMaterialsDisplay = (config: ChallengeConfiguration) => {
    return getChallengeMaterialDisplayItems(config);
};

type UpdateKey = 'destinationTank' | 'destinationGroup' | 'challengeDuration' | 'targetAmount' | 'instructions';

const handleFieldChange = async (configIndex: number, event: { index: number; key: UpdateKey; item: unknown }) => {
    const { index, key, item } = event;
    const config = props.configurations[configIndex];
    const allConfigIndex = allConfigurations.value.findIndex(c => c.key === config.key);
    if (allConfigIndex === -1) return;
    const oldValue = config[key as keyof ChallengeConfiguration];

    if (JSON.stringify(oldValue) === JSON.stringify(item)) {
        return;
    }

    // Set loading state
    isUpdating.value[configIndex] = true;

    try {
        const requireJustification = ['destinationTank', 'destinationGroup'].includes(key);

        const success = await recordChange({
            fieldPath: `configurations[${allConfigIndex}].${key}`,
            fieldName: getFieldLabel(key),
            oldValue,
            newValue: item,
            context: 'ChallengeConfiguration',
            contextType: 'edit',
            workflowStage: 'setup',
            requireJustification,
            justification: ''
        });

        if (success) {
            await updateConfiguration(allConfigIndex, index, key, item);

            if (key === 'destinationTank') {
                updateTankGroups(configIndex, allConfigIndex, item as Tank);
            }

            // Show success notification
            showSuccess(
                "Configuration Updated", 
                `${getFieldLabel(key)} has been updated successfully.`
            );
        }
    } catch (error) {
        console.error('Error updating configuration:', error);
        showError(
            "Update Failed", 
            `Failed to update ${getFieldLabel(key)}. Please try again.`
        );
    } finally {
        // Clear loading state
        isUpdating.value[configIndex] = false;
    }
};

// Update tank groups when destination tank changes
const updateTankGroups = (configIndex: number, allConfigIndex: number, tank: Tank) => {
    const tankWithGroups = props.tanks.find(
        t => t.key === tank?.key
    );

    if (tankWithGroups?.groups) {
        tankGroups.value[configIndex.toString()] = tankWithGroups.groups;
    } else {
        tankGroups.value[configIndex.toString()] = [];
    }

    // Clear destination group when tank changes
    updateConfiguration(allConfigIndex, -1, 'destinationGroup', null);
};

// Format for display purposes
const getFieldLabel = (field: string): string => {
    const fieldLabels: Record<string, string> = {
        'destinationTank': 'Destination Tank',
        'destinationGroup': 'Destination Group',
        'tag': 'Tag Color',
        'challengeDuration': 'Challenge Duration'
    };

    return fieldLabels[field] || field;
};

// Toggle dropdown
const toggleDropdown = (configIndex: number) => {
    dropdownOpen.value[configIndex] = !dropdownOpen.value[configIndex];
    // Close other dropdowns
    Object.keys(dropdownOpen.value).forEach(key => {
        if (parseInt(key) !== configIndex) {
            dropdownOpen.value[parseInt(key)] = false;
        }
    });
};

// Close dropdown when clicking outside
const closeDropdown = (configIndex: number) => {
    dropdownOpen.value[configIndex] = false;
};

// Close all dropdowns
const closeAllDropdowns = () => {
    Object.keys(dropdownOpen.value).forEach(key => {
        dropdownOpen.value[parseInt(key)] = false;
    });
};

// Handle clicks outside dropdown
const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-container')) {
        closeAllDropdowns();
    }
};

// Handle tag selection from custom dropdown
const selectTag = async (configIndex: number, tag: GroupTag) => {
    const oldTag = selectedTags.value[configIndex];
    const config = props.configurations[configIndex];
    const allConfigIndex = allConfigurations.value.findIndex(c => c.key === config.key);
    if (allConfigIndex === -1) return;
    
    // Set loading state
    isUpdating.value[configIndex] = true;

    try {
        const success = await recordChange({
            fieldPath: `configurations[${configIndex}].selectedTag`,
            fieldName: `Tag (${tag.title})`,
            oldValue: {
              key: oldTag?.key || 'None',
              value: oldTag?.title || 'None',
            },
            newValue: {
              key: tag.key,
              value: tag.title,
            },
            context: 'ChallengeConfiguration',
            contextType: 'edit',
            workflowStage: 'setup',
            requireJustification: true,
            justification: ''
        });

        if (success) {
            selectedTags.value[configIndex] = tag;

            // Update destination group tags to include the new tag (and remove any previous "new tag")
            const sourceTags = config.sourceGroup?.tags || [];
            const sourceTagTitles = new Set(
                sourceTags
                    .filter(t => t.title !== 'Start Tag')
                    .map(t => t.title)
            );

            const updatedDestGroup = config.destinationGroup
                ? { ...config.destinationGroup }
                : { key: '', name: '', tags: [] } as TankGroup;

            if (!updatedDestGroup.tags) {
                updatedDestGroup.tags = [];
            } else {
                updatedDestGroup.tags = [...updatedDestGroup.tags];
            }

            updatedDestGroup.tags = updatedDestGroup.tags.filter(t =>
                sourceTagTitles.has(t.title) || t.title === tag.title
            );

            const tagExists = updatedDestGroup.tags.some(t => t.title === tag.title);
            if (!tagExists) {
                updatedDestGroup.tags.push(tag);
            }

            await updateConfiguration(allConfigIndex, -1, 'destinationGroup', updatedDestGroup);
            
            // Show success notification
            showSuccess(
                "Tag Updated", 
                `Tag has been changed to "${tag.title}".`
            );
        }
    } catch (error) {
        console.error('Error updating tag:', error);
        showError(
            "Tag Update Failed", 
            "Failed to update tag. Please try again."
        );
    } finally {
        // Clear loading state
        isUpdating.value[configIndex] = false;
    }
    
    closeDropdown(configIndex);
};

// Watch for configuration changes to load tank groups
watch(() => props.configurations, (configs) => {
    if (configs) {
        configs.forEach((config, index) => {
            if (config.destinationTank?.key) {
                const tankWithGroups = props.tanks.find(
                    t => t.key === config.destinationTank?.key
                );
                if (tankWithGroups?.groups) {
                    tankGroups.value[index.toString()] = tankWithGroups.groups;
                }
            }

            // Initialize selected tag from destination group (new tag)
            if (!selectedTags.value[index]) {
                const newTag = findNewTag(config);
                if (newTag) {
                    selectedTags.value[index] = newTag;
                }
            }
        });
    }
}, { immediate: true });

// Add/remove event listeners
onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <div class="w-full">
        <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table class="min-w-full divide-y divide-gray-200">
                <thead>
                <tr>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Source Tank</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Source Group</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Destination Tank</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Challenge Material</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Method</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Dose (mL)</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Tag</th>
                    <th class="bg-gray-50 px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">Target Amount</th>
                </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white">
                    <template v-for="(config, index) in configurations" :key="config.key">
                    <tr class="hover:bg-gray-50 transition-colors duration-150 relative">
                        <!-- Loading overlay for the entire row -->
                        <td v-if="isUpdating[index]" colspan="8" class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                            <div class="flex items-center space-x-2">
                                <svg class="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="text-sm text-gray-600">Updating...</span>
                            </div>
                        </td>

                        <!-- Source Tank (readonly) -->
                        <td class="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {{ config.sourceTank?.name || config.sourceTank?.label || 'None' }}
                        </td>

                        <!-- Source Group (readonly) - Show tags instead of group name -->
                        <td class="px-6 py-4 text-sm">
                            <div class="flex flex-wrap gap-1">
                                <span
                                    v-for="tag in (config.sourceGroup?.tags || []).filter((tag: GroupTag) => tag.title !== 'Start Tag')"
                                    :key="tag.key"
                                    class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                                    :style="{
                                        backgroundColor: tag.backgroundColor || '#e5e7eb',
                                        color: tag.textColor || '#374151'
                                    }"
                                >
                                    {{ tag.title }}
                                </span>
                                <span
                                    v-if="!config.sourceGroup?.tags || config.sourceGroup.tags.filter((tag: GroupTag) => tag.title !== 'Start Tag').length === 0"
                                    class="inline-block px-2 py-0.5 text-xs font-medium rounded-full opacity-75 bg-gray-400 text-gray-700"
                                >
                                    Untagged
                                </span>
                            </div>
                        </td>

                        <!-- Destination Tank (editable) -->
                        <td class="px-6 py-4 text-sm">
                            <ItemDropdown
                                :index="-1"
                                :label="''"
                                :items="tanks"
                                :itemKey="'destinationTank'"
                                :selectedItem="config.destinationTank"
                                :disabled="isUpdating[index]"
                                @fieldChange="(event: any) => handleFieldChange(index, event)"
                            />
                        </td>

                        <!-- Challenge Material (readonly) -->
                        <td class="px-6 py-4 text-sm text-gray-900">
                            <div v-if="getMaterialsDisplay(config).length > 0">
                                <div v-for="(item, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-1">
                                    {{ item.name }}
                                </div>
                            </div>
                            <div v-else class="text-gray-500">None</div>
                        </td>

                        <!-- Method (readonly) -->
                        <td class="px-6 py-4 text-sm text-gray-900">
                            <div v-if="getMaterialsDisplay(config).length > 0">
                                <div v-for="(material, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-1">
                                    {{ material.method }}
                                </div>
                            </div>
                            <div v-else class="text-gray-500">None</div>
                        </td>

                        <!-- Dose (readonly) -->
                        <td class="px-6 py-4 text-sm text-gray-900">
                            <div v-if="getMaterialsDisplay(config).length > 0">
                                <div v-for="(material, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-1">
                                    {{ material.dose }}
                                </div>
                            </div>
                            <div v-else class="text-gray-500">N/A</div>
                        </td>

                        <!-- Tag selection -->
                        <td class="whitespace-nowrap px-6 py-4 text-sm">
                            <div class="relative dropdown-container">
                                <button 
                                    @click="toggleDropdown(index)"
                                    :disabled="isUpdating[index]"
                                    class="flex items-center justify-between w-full min-w-[150px] text-sm border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div class="flex items-center flex-1">
                                        <!-- Show single selected tag or placeholder -->
                                        <span 
                                            v-if="selectedTags[index]"
                                            class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                                            :style="{ 
                                                backgroundColor: selectedTags[index]?.backgroundColor || '#e5e7eb', 
                                                color: selectedTags[index]?.textColor || '#374151' 
                                            }"
                                        >
                                            {{ selectedTags[index]?.title }}
                                        </span>
                                        <span v-else class="text-gray-500">Select a tag...</span>
                                    </div>
                                    <svg class="h-4 w-4 text-gray-500 ml-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div v-if="dropdownOpen[index]" class="absolute z-10 mt-2 w-full min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200">
                                    <div class="py-2 max-h-48 overflow-y-auto">
                                        <button 
                                            v-for="tag in availableTags.filter((tag: GroupTag) => tag.title !== 'Start Tag')" 
                                            :key="tag.key" 
                                            @click="selectTag(index, tag)"
                                            :disabled="isUpdating[index]"
                                            class="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span 
                                                class="inline-block px-2 py-1 text-xs font-medium rounded-full"
                                                :style="{ 
                                                    backgroundColor: tag.backgroundColor || '#e5e7eb', 
                                                    color: tag.textColor || '#374151' 
                                                }"
                                            >
                                                {{ tag.title }}
                                            </span>
                                        </button>
                                        <div v-if="availableTags.filter((tag: GroupTag) => tag.title !== 'Start Tag').length === 0" class="px-3 py-2 text-sm text-gray-500">
                                            No tags available ({{ availableTags.length }} total tags)
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- Target Amount (readonly) -->
                        <td class="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900 text-center">
                            {{ config.targetAmount || 0 }}
                        </td>
                    </tr>
                    
                    <!-- Special Instructions Row -->
                    <tr v-if="config.instructions" class="bg-gray-50">
                        <td
                            colspan="8"
                            :class="showInstructions[index] ? 'px-6 py-4' : 'px-6 py-1.5'"
                        >
                            <button
                                @click="toggleInstructions(index)"
                                class="w-full flex items-center justify-between text-left hover:bg-gray-100 rounded transition-colors duration-200"
                                :class="showInstructions[index] ? 'p-3' : 'p-1.5'"
                            >
                                <div class="flex items-center gap-2">
                                    <svg class="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                    </svg>
                                    <span :class="showInstructions[index] ? 'text-sm font-semibold text-red-600' : 'text-xs font-semibold text-red-600'">
                                        Special Instructions
                                    </span>
                                </div>
                                <svg 
                                    class="w-4 h-4 text-gray-500 transition-transform duration-200"
                                    :class="{ 'rotate-180': showInstructions[index] }"
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div
                                v-show="showInstructions[index]"
                                class="mt-2 p-4 bg-red-50 border border-red-200 rounded-lg transition-all duration-300 ease-in-out"
                            >
                                <p class="text-sm text-red-700 leading-relaxed whitespace-pre-wrap">
                                    {{ config.instructions }}
                                </p>
                            </div>
                        </td>
                    </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <JustificationModal />
    </div>
</template>
